import React, { useState } from 'react';
import { X, Search } from 'lucide-react';
import { userLookupService } from '../services/userLookupService';
import { authService } from '../services/authService';
import { FindUserResponse } from '../types';
import { useTranslation } from 'react-i18next';
import { formatLicensePlate } from '../utils/formatters';

interface FindUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserSelect?: (user: any) => void; // Callback for when user is selected in kiosk mode
  chargerName?: string;
}

export const FindUserModal: React.FC<FindUserModalProps> = ({
  isOpen,
  onClose,
  onUserSelect,
  chargerName
}) => {
  const { t } = useTranslation();
  const [licensePlate, setLicensePlate] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<FindUserResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Check if we're in kiosk mode
  const isKioskMode = authService.isKioskUser();

  const handleUserSelect = () => {
    if (searchResult?.searchedUser && onUserSelect) {
      onUserSelect(searchResult.searchedUser);
      handleClose();
    }
  };

  const handleSearch = async () => {
    const digitsOnly = licensePlate.replace(/\D/g, '');
    if (digitsOnly.length !== 8) {
      setSearchResult({
        isSuccess: false,
        errorMessage: 'License plate must be exactly 8 digits (format: xxx-xx-xxx)',
        notificationSent: false
      });
      setHasSearched(true);
      return;
    }

    setIsSearching(true);
    setHasSearched(false);
    setSearchResult(null);

    try {
      const result = await userLookupService.findUserByLicensePlate(licensePlate);
      setSearchResult(result);
      setHasSearched(true);
    } catch (error) {
      console.error('Find user search failed:', error);
      setSearchResult({
        isSuccess: false,
        errorMessage: t('findUser.searchError'),
        notificationSent: false
      });
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setLicensePlate('');
    setSearchResult(null);
    setHasSearched(false);
    onClose();
  };

  const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatLicensePlate(e.target.value);
    setLicensePlate(formatted);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching && licensePlate.trim()) {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern">
        <div className="modal-header-modern">
          <h3 className="modal-title">{t('findUser.title')}</h3>
          {chargerName && (
            <p className="modal-subtitle">{t('findUser.searchingFor', { chargerName })}</p>
          )}
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSearching}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-content-modern">
          {/* License plate input */}
          <div className="form-field-modern">
            <label className="form-label-modern">
              {t('findUser.licensePlateLabel')}
            </label>
            <input
              type="text"
              value={licensePlate}
              onChange={handleLicensePlateChange}
              onKeyPress={handleKeyPress}
              placeholder={t('findUser.licensePlatePlaceholder')}
              className="form-input-modern"
              disabled={isSearching}
              pattern="\d{3}-\d{2}-\d{3}"
              title="License plate must be in format nnn-nn-nnn (e.g., 123-45-678)"
            />
            <p className="form-help-text-modern">
              Auto-formatted as you type. Enter digits only: 12345678 → 123-45-678
            </p>
          </div>

          {/* Search button */}
          <div className="form-field-modern">
            <button
              onClick={handleSearch}
              disabled={licensePlate.replace(/\D/g, '').length !== 8 || isSearching}
              className="btn-modern btn-primary-modern w-full"
            >
              {isSearching ? (
                <>
                  <div className="loading-spinner-modern" />
                  {t('findUser.searching')}
                </>
              ) : (
                <>
                  <Search size={16} />
                  {t('findUser.searchButton')}
                </>
              )}
            </button>
          </div>

          {/* Search results */}
          {hasSearched && searchResult && (
            <div className="search-results-section">
              {searchResult.isSuccess ? (
                <div className="success-card-modern">
                  <div className="success-header">
                    <div className="success-icon">✅</div>
                    <h4 className="success-title">{t('findUser.userFound')}</h4>
                  </div>
                  
                  {searchResult.searchedUser && (
                    <div className="user-info-card">
                      <div className="user-info">
                        <div className="user-name">{searchResult.searchedUser.fullName}</div>
                        <div className="user-details-vertical">
                          <div className="detail-item">
                            <span className="detail-label">{t('findUser.phoneNumber')}:</span>
                            <span className="detail-text">📞 </span>
                            <a 
                              href={`tel:${searchResult.searchedUser.phoneNumber.replace(/[^\d+]/g, '')}`}
                              className="phone-link inline"
                              title={`Call ${searchResult.searchedUser.fullName}`}
                              onClick={(e) => {
                                // Don't prevent default - let the tel: link work naturally
                                e.stopPropagation();
                                if (searchResult.searchedUser) {
                                  const cleanPhone = searchResult.searchedUser.phoneNumber.replace(/[^\d+]/g, '');
                                  console.log('Phone number clicked:', {
                                    original: searchResult.searchedUser.phoneNumber,
                                    cleaned: cleanPhone,
                                    telUrl: `tel:${cleanPhone}`
                                  });
                                  
                                  // For debugging - also try window.open as fallback
                                  setTimeout(() => {
                                    try {
                                      if (window.navigator && window.navigator.userAgent.includes('Mobile')) {
                                        console.log('Mobile device detected - tel: link should work');
                                      }
                                    } catch (err) {
                                      console.log('Browser detection failed:', err);
                                    }
                                  }, 100);
                                }
                              }}
                            >
                              {searchResult.searchedUser.phoneNumber}
                            </a>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t('findUser.licensePlate')}:</span>
                            <span className="detail-text">🚗 {searchResult.searchedUser.licensePlate}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {searchResult.notificationSent && (
                    <div className="notification-status">
                      <div className="notification-icon">✅</div>
                      <p className="notification-text">
                        {t('findUser.notificationSent')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="modern-error-card">
                  <div className="error-card-header">
                    <div className="error-icon">❌</div>
                    <h4 className="error-title">{t('findUser.userNotFound')}</h4>
                  </div>
                  
                  {searchResult.errorMessage && (
                    <p className="error-description">
                      {searchResult.errorMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions-modern">
          {isKioskMode && searchResult?.isSuccess && searchResult.searchedUser ? (
            <>
              <button
                onClick={handleClose}
                className="btn-modern btn-secondary-modern"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUserSelect}
                className="btn-modern btn-primary-modern"
              >
                Select User
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="btn-modern btn-secondary-modern"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};